import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import type { Edge } from 'reactflow';
import type { User } from '../../../util/Types/User';
import TagInput from '../../Test/TagInput';
import Label from '../../UI/Label/Label';
import { isActiveEdge } from '../../../util/findActiveHandle';
import type { McpToolItem } from '../../../service/McpServerService';
import type { McpNodeFormState } from '../../../types/mcpNode';
import {
  MCP_TAG_FIELD_HEIGHT,
  MCP_TAG_INPUT_WIDTH,
} from './mcpNodeUiConstants';

function buildSelectSx(theme: User['theme']) {
  return {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      minHeight: 40,
    },
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      py: 1,
    },
    '& .MuiList-root': {
      backgroundColor: theme === 'dark' ? '#101828' : '#F9FAFB',
    },
    '& .MuiSvgIcon-root': {
      color: theme === 'dark' ? '#D0D5DD' : '#101828',
    },
    borderRadius: '8px',
    border: 'none',
    boxShadow: 'none',
    '.MuiOutlinedInput-notchedOutline': { border: 0 },
    bgcolor: theme === 'dark' ? '#101828' : '#F9FAFB',
    color: theme === 'dark' ? '#D0D5DD' : '#101828',
  };
}

export interface McpToolPanelProps {
  user: User | null;
  nodeId: string;
  edges: Edge[];
  data: Record<string, unknown>;
  formData: McpNodeFormState;
  availableTools: McpToolItem[];
  isLoadingTools: boolean;
  rawMcpServerUrl: string;
  organizationId: string;
  canLoadTools: boolean;
  onLoadTools: () => void;
  onToolSelect: (name: string) => void;
  onChange: (e: { target: { name: string; value: string } }) => void;
  findColor: Record<string, string> | undefined;
}

const McpToolPanel = ({
  user,
  nodeId,
  edges,
  data,
  formData,
  availableTools,
  isLoadingTools,
  rawMcpServerUrl,
  organizationId,
  canLoadTools,
  onLoadTools,
  onToolSelect,
  onChange,
  findColor,
}: McpToolPanelProps) => {
  const selectSx = buildSelectSx(user?.theme ?? 'light');

  return (
    <Box>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{ display: 'block', mt: 2, mb: 1.5 }}
      >
        Tool
      </Typography>
      <Box sx={{ mb: 1.5 }}>
        <Button
          variant="outlined"
          onClick={onLoadTools}
          disabled={isLoadingTools || !canLoadTools}
          fullWidth
          sx={{ mb: 1 }}
        >
          {isLoadingTools ? (
            <CircularProgress size={20} />
          ) : (
            'Load tools from URL'
          )}
        </Button>
        {availableTools.length > 0 ? (
          <FormControl fullWidth sx={{ mb: 1 }}>
            <Label
              labelName="Select tool"
              isAdvanced
              labelStyle={{
                fontSize: '14px',
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                fontWeight: 400,
              }}
            />
            <Select
              className="nodrag"
              value={formData.toolName || ''}
              label="Select tool"
              sx={selectSx}
              onChange={e => onToolSelect(e.target.value as string)}
              renderValue={v => {
                const t = availableTools.find(tool => tool.name === v);
                return t ? t.title || t.name : v || '';
              }}
              MenuProps={{
                disableScrollLock: true,
                sx: { zIndex: 9999 },
                PaperProps: {
                  sx: { zIndex: 9999, maxHeight: 320 },
                },
              }}
            >
              <MenuItem value="">
                <em>Select a tool</em>
              </MenuItem>
              {availableTools.map(t => (
                <MenuItem key={t.name} value={t.name}>
                  <ListItemText
                    primary={t.title || t.name}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Optional — if not selected, the best tool is chosen from your
              prompt. {availableTools.length} tool(s) on this server.
            </FormHelperText>
          </FormControl>
        ) : (
          <FormHelperText sx={{ mb: 1 }}>
            {rawMcpServerUrl.trim() ||
            (formData.integrationId?.trim() && organizationId)
              ? 'Click Load tools to fetch available tools.'
              : 'Enter MCP Server URL or select a saved MCP (HTTP) connection.'}
          </FormHelperText>
        )}
        {data?.toolNameData &&
        Array.isArray(data.toolNameData) &&
        data.toolNameData.length ? (
          <TagInput
            name="toolName"
            placeholder="Tool name from connection"
            labelName="Tool Name (from connection)"
            handleId="toolName"
            width={MCP_TAG_INPUT_WIDTH}
            height={MCP_TAG_FIELD_HEIGHT}
            isCollapsed={true}
            nodeId={nodeId}
            isActive={isActiveEdge(edges, nodeId, 'toolName', 'target')}
            isAdvanced={true}
            findColor={findColor}
            removeIcons
            advancedCollapsed={true}
            onChange={onChange}
            dataConnected={data.toolNameData as { text: string }[]}
            placement={data?.placement as string | undefined}
            value={formData.toolName}
            nodeLabel={data?.label as string | undefined}
          />
        ) : availableTools.length === 0 ? (
          <TagInput
            name="toolName"
            placeholder="Tool name (optional)"
            labelName="Tool Name"
            handleId="toolName"
            width={MCP_TAG_INPUT_WIDTH}
            height={MCP_TAG_FIELD_HEIGHT}
            isCollapsed={true}
            nodeId={nodeId}
            isActive={isActiveEdge(edges, nodeId, 'toolName', 'target')}
            isAdvanced={true}
            findColor={findColor}
            removeIcons
            advancedCollapsed={true}
            onChange={onChange}
            dataConnected={(data?.toolNameData as { text: string }[]) || []}
            placement={data?.placement as string | undefined}
            value={formData.toolName}
            nodeLabel={data?.label as string | undefined}
          />
        ) : null}
      </Box>
    </Box>
  );
};

export default McpToolPanel;
